import React, { useEffect, useState, ChangeEvent } from 'react';
import { PasslyTab, PasslyTabs, PasslyTabContent, PasslyButton, confirm, PageActions } from '@app/components';
import SwipeableViews from 'react-swipeable-views';
import { useTypedTranslation } from '@app/hooks';
import {
    ApplicationConfiguration,
    WorkflowAppConfiguration,
    ProtocolConfiguration,
    AttributeTransform,
    AppPermissions,
    AppSigningEncryption,
    Federation,
    Synchronization,
    IOffice365State
} from './addEditAppTabs';
import { Grid, Box } from '@material-ui/core';
import { AppStageType, MODULE_NAME, PROTOCOL_TYPES, suppliedCredentialTypes } from '@app/pages/ssoManager/addEditApplication.enums';
import {
    isWorkflowApp,
    isCatalogApp,
    isCustomApp,
    loadWorkflowSuppliedCredential,
    isTemplate,
    isKaseyaOne
} from '@app/helpers/addEditApplication.helpers';
import { AppsProvider, ApplicationInterfaceTypeUnion } from './addEditAppTabs/appContext';
import { applicationAttributeModel } from '@app/helpers';
import { IPatchApplication, IApplicationAttribute, ILocalLicense, IUsageLocation } from '@app/models';
import { appLibraryService, basicService, applicationsService } from '@app/services';
import { useSelector, useDispatch } from 'react-redux';
import { addAppFinished, getLoaderBtn } from '@app/store/actions';
import {AxiosError} from 'axios';
import {handleSetSnackbar} from '@app/store/actions/snackbar.actions';

interface IProps {
    appStageType: number;
    app: ApplicationInterfaceTypeUnion;
    closeListModal: Function;
}

export const AddEditApp = ({ appStageType, app, closeListModal }: IProps) => {
    const { translation } = useTypedTranslation();

    const [tabIndex, setTabIndex] = useState(0);
    const [appState, setAppState] = useState<any>(app);
    const [errors, setErrors] = useState(false);
    const [protocolConfigError, setProtocolConfigError] = useState(false);
    const [federationError, setFederationError] = useState(false);
    const [isDomainChanged, setIsDomainChanged] = useState(false);

    // handles workflow values
    const [workflowValues, setWorkflowValues] = useState<any>({});
    const [suppliedCredential, setSuppliedCredential] = useState<string>(loadWorkflowSuppliedCredential(app.Attributes));

    const editingApp = appStageType === AppStageType.editApp;
    const addingApp = appStageType === AppStageType.addApp;

    const showFederationError = editingApp && isTemplate(app) && federationError;

    const defaultLicense: ILocalLicense = {
        label: translation('MSOL_DO_NOT_LICENSE'),
        value: 'none'
    };

    const defaultLocation: IUsageLocation = {
        name: translation('MSOL_DO_NOT_LOCATION'),
        id: 'none'
    };

    const initialState: IOffice365State = {
        managedSetup: {
            managed: true,
            active: true,
            domain: '',
            domainGraphModule: '',
            username: '',
            password: '',
            domainResults: [],
            graphApiEnabled: false,
            graphApiTenantId: '',
            graphApiTenantIdGraphModule: '',
            graphApiClientId: '',
            graphApiClientIdGraphModule: ''
        },
        licenses: [defaultLicense],
        selectedLicense: defaultLicense.value,
        locations: [defaultLocation],
        usageLocation: 'none',
        canConfigure: false,
        syncSources: [],
        selectedSyncSource: 'PrincipalName',
        membershipSyncMode: 'AddMembersOnly',
        selectedGroupTypeSyncMode: 'IfTypeMatches',
        removeGroupDuplicates: false,
        forceNewGroupCreation: true,
        syncGroupsEnabled: true,
        importMsolEnabled: true,
        importMsolUsersEnabled: true,
        importMsolGroupsEnabled: true,
        importedUsersInitialGroupId: '',
        importedUsersProvisioningPolicyId: '',
        lastSyncTime: null,
        federationType: ''
    };
    const [office365ConfigOptions, setOffice365ConfigOptions] = useState(initialState);
    const loaderBtnState: boolean = useSelector((state: any) => state.modal.modalLoaderBtn);

    const dispatch = useDispatch();

    const updateNewValue = (stateKeyName: string, value: string) => {
        setAppState((prevState: any) => ({ ...prevState, [stateKeyName]: value }));
    };

    const appMainState = {
        ...appState,
        Protocol: appState.Protocol ?? PROTOCOL_TYPES(translation)[0].value,
        updateState: updateNewValue
    };

    useEffect(() => {
        if (!app.IconUrl) app.IconUrl = `${window.location.origin}/assets/images/apps/app-large.png`;

        if (editingApp && isWorkflowApp(app) && suppliedCredential === suppliedCredentialTypes.admin) {
            applicationsService.getAdminSuppliedCredentials(app.Id, handleAdminSuppliedCredentials);
        }
    }, []);

    const handleAdminSuppliedCredentials = (response: any) => {
        const suppliedCredential = response.data;

        if (suppliedCredential && suppliedCredential.HasCredentials) {
            setWorkflowValues(JSON.parse(suppliedCredential.WorkflowValues));
        }
    };

    const handleChange = (event: ChangeEvent<{}>, newValue: number) => {
        setTabIndex(newValue);
    };

    const handleChangeIndex = (index: number) => {
        setTabIndex(index);
    };

    const handleConfigurationUpdate = (newData: any) => {
        setAppState((prevState: any) => ({ ...prevState, ...newData }));
    };

    const configTabs = [
        {
            label: translation('APP_FEDERATION_TAB'),
            content: (
                <Federation
                    setFederationError={setFederationError}
                    isInvisible={addingApp}
                    state={office365ConfigOptions}
                    setState={setOffice365ConfigOptions}
                    editingApp={editingApp}
                    version={appState.Version}
                    setIsDomainChanged={setIsDomainChanged}
                />
            ),
            rendered: isTemplate(app),
            isInvisible: addingApp
        },
        {
            label: translation('APP_SYNCHRONIZATION_TAB'),
            content: <Synchronization isInvisible={addingApp} state={office365ConfigOptions} setState={setOffice365ConfigOptions} />,
            rendered: isTemplate(app),
            isInvisible: addingApp
        },
        {
            label: translation('APP_CONFIGURATION_TAB'),
            content: (
                <ApplicationConfiguration
                    isWorkflowApp={() => isWorkflowApp(app)}
                    isCatalogApp={() => isCatalogApp(app)}
                    update={handleConfigurationUpdate}
                    error={setErrors}
                    suppliedCredential={suppliedCredential}
                    handleSuppliedCredentialChange={setSuppliedCredential}
                    workflowValues={workflowValues}
                    handleWorkflowValuesChange={setWorkflowValues}
                    addingApp={addingApp}
                />
            ),
            rendered: true,
            isInvisible: false
        },
        {
            label: translation('WORKFLOW_APP_CONFIG_TAB'),
            content: <WorkflowAppConfiguration Workflow={appState.Workflow} />,
            rendered: isWorkflowApp(app),
            isInvisible: false
        },
        {
            label: translation('PROTOCOL_CONFIGURATION_TAB'),
            content: <ProtocolConfiguration setProtocolConfigError={setProtocolConfigError} isOffice365={isTemplate(app)} />,
            rendered: !isWorkflowApp(app),
            isInvisible: false
        },
        {
            label: translation('ATTRIBUTE_TRANSFORM_TAB'),
            content: <AttributeTransform />,
            rendered: !isWorkflowApp(app),
            isInvisible: false
        },
        // { // THIS TAB IS OMMITED
        //     label: translation('SEED_GENERATOR'),
        //     content: <SeedGenerator app={app}/>,
        //     rendered: !isWorkflowApp() && isCatalogApp()
        // },
        {
            label: translation('APP_ACCESS'),
            content: <AppPermissions />,
            rendered: !isCustomApp(app),
            isInvisible: false
        },
        {
            label: translation('APP_SIGNING_TAB'),
            content: <AppSigningEncryption />,
            rendered: !isCustomApp(app) && !isWorkflowApp(app),
            isInvisible: false
        }
    ];

    const generateWorkflowAttributes = (): IApplicationAttribute[] => {
        const suppliedCredentialAttribute: IApplicationAttribute = {
            '@odata.type': applicationAttributeModel,
            Key: 'suppliedCredential',
            Value: suppliedCredential
        };

        if (suppliedCredential === suppliedCredentialTypes.admin) {
            const workflowValuesAttribute: IApplicationAttribute = {
                '@odata.type': applicationAttributeModel,
                Key: 'workflow:values',
                Value: JSON.stringify(workflowValues)
            };

            return [suppliedCredentialAttribute, workflowValuesAttribute];
        } else {
            return [suppliedCredentialAttribute];
        }
    };

    const saveButtonClick = () => {
        if (isDomainChanged) {
            confirm(
                translation('CHANGE_DOMAIN_CONFIRMATION_DESCRIPTION'),
                translation('CHANGE'),
                translation('CANCEL'),
                translation('CHANGE_DOMAIN_CONFIRMATION_TITLE'),
                false,
                {
                    isDeleteAction: true
                }
            ).then(
                () => {
                    saveApp();
                },
                () => {
                    // do nothing on cancel
                }
            );
        } else {
            saveApp();
        }
    };

    const saveApp = () => {
        const appConfig = appMainState;

        if (office365ConfigOptions.federationType) {
            let config: any = {};

            if (appConfig.ServiceConfiguration) {
                config = JSON.parse(appConfig.ServiceConfiguration);
            }

            if (office365ConfigOptions.federationType === MODULE_NAME.msolModule) {
                if ('graph-configuration' in config) {
                    config['msol-configuration'] = config['graph-configuration'];
                    delete config['graph-configuration'];
                }
            } else {
                if ('msol-configuration' in config) {
                    config['graph-configuration'] = config['msol-configuration'];
                    delete config['msol-configuration'];
                }
            }

            config.template = office365ConfigOptions.federationType;
            appConfig.ServiceConfiguration = JSON.stringify(config);
        }

        const patchAppConfig: IPatchApplication = {
            '@odata.context': appConfig['@odata.context'],
            Id: appConfig.Id,
            OrganizationId: appConfig.OrganizationId,
            DisplayName: appConfig.DisplayName,
            Description: appConfig.Description,
            IsSystemApplication: appConfig.IsSystemApplication,
            Enabled: appConfig.Enabled,
            SupportsRefreshToken: appConfig.SupportsRefreshToken,
            SupportsConsent: appConfig.SupportsConsent,
            Protocol: appConfig.Protocol,
            IdentityIssuerUri: appConfig.IdentityIssuerUri,
            ServiceIssuerUri: appConfig.ServiceIssuerUri,
            AccessTokenLifetime: appConfig.AccessTokenLifetime,
            RefreshTokenLifetime: appConfig.RefreshTokenLifetime,
            ServiceConfiguration: appConfig.ServiceConfiguration,
            ReplyToUrl: appConfig.ReplyToUrl,
            AttributeRules: appConfig.AttributeRules,
            IconUrl: appConfig.IconUrl,
            PolicyId: appConfig.PolicyId,
            Version: appConfig.Version,
            '@odata.type': appConfig['@odata.type']
        };

        let audiences: any[] = appConfig.AudienceUris.map((audienceUri: string) => {
            return { Uri: audienceUri };
        });

        if (audiences.length === 0) {
            // Reply To URL/Assertion consumer service url should be used as default
            // if none are provided
            audiences = [{ Uri: appConfig.ReplyToUrl }];
        }

        dispatch(getLoaderBtn(true));
        if (addingApp) {
            let addAppConfig: any = patchAppConfig;
            delete addAppConfig['@odata.context'];

            if (isWorkflowApp(app)) {
                addAppConfig['@odata.type'] = '#Kaseya.AuthAnvil.Models.WorkflowApplication';
                addAppConfig.Attributes = generateWorkflowAttributes();
                addAppConfig.CatalogId = appConfig.CatalogId;
                addAppConfig.CategoryId = appConfig.CategoryId;
                addAppConfig.CategoryName = appConfig.CategoryName;
                addAppConfig.Company = appConfig.Company;
                addAppConfig.OldestVersion = appConfig.OldestVersion;
                addAppConfig.Version = appConfig.Version;
                addAppConfig.Workflow = appConfig.Workflow;
            } else {
                addAppConfig.Audiences = audiences;
                addAppConfig['@odata.type'] = '#Kaseya.AuthAnvil.Models.Application';
                addAppConfig.Attributes = [];
            }

            appLibraryService.addCatalogApp(addAppConfig, handleAppSaveError, handleAddAppSuccess);
        } else {
            if (isWorkflowApp(app)) {
                const updatedAttributes = generateWorkflowAttributes();
                applicationsService.updateSuppliedCredential(app.Id, { applicationAttributes: updatedAttributes });
            } else {
                appLibraryService.updateAppAudienceUris(app.Id, audiences);
            }

            appLibraryService.updateApp(app.Id, patchAppConfig, handleAppSaveError, handleAppSaveSuccess);
        }
    };

    const handleAppSaveError = (error: AxiosError<any>) => {
        dispatch(getLoaderBtn(false));

        if (error?.response?.status === 400 && isKaseyaOne(appMainState.ServiceConfiguration)) {
            closeListModal();
            dispatch(handleSetSnackbar({ show: true, message: translation('KASEYAONE_ONLY_ONE_APP_ERROR'), severity: 'error' }));
        }
    };

    const redirectAfterAdd = (appId: string) => {
        basicService.redirect(`/sso/library/details/${appId}`);
    };

    const handleAddAppSuccess = (response: any) => {
        if (isTemplate(app)) {
            // let msol component handle msol APIs
            dispatch(addAppFinished(true, response.data.Id));
        } else {
            dispatch(getLoaderBtn(false));
            redirectAfterAdd(response.data.Id);
        }
    };

    const handleAppSaveSuccess = () => {
        if (isTemplate(app)) {
            // let msol component handle msol APIs
            dispatch(addAppFinished(true, app.Id));
        } else {
            dispatch(getLoaderBtn(false));
            basicService.redirect('/sso/library');
        }
    };

    return (
        <>
        <AppsProvider value={appMainState}>
            <PasslyTabs className='tab-labels' value={tabIndex} onChange={handleChange}>
                {configTabs
                    .filter((tab) => tab.rendered && !tab.isInvisible)
                    .map(({ label }: any, index: number) => (
                        <PasslyTab className='tab__label' label={label} key={index} />
                    ))}
            </PasslyTabs>
            <SwipeableViews
                style={{ height: `${addingApp ? '63vh' : 'auto'}` }}
                className='tab-contents'
                index={tabIndex}
                onChangeIndex={handleChangeIndex}
            >
                {configTabs
                    .filter((tab) => tab.rendered && !tab.isInvisible)
                    .map(({ content }: any, index: number) => (
                        <PasslyTabContent value={tabIndex} index={index} key={index}>
                            {content}
                        </PasslyTabContent>
                    ))}

                {configTabs
                    .filter((tab) => tab.rendered && tab.isInvisible)
                    .map(({ content }: any, index: number) => (
                        <PasslyTabContent value={-1} index={index} key={index}>
                            {content}
                        </PasslyTabContent>
                    ))}
            </SwipeableViews>

            {editingApp && (
                <PageActions
                    primaryBtn={translation('SAVE_CHANGES')}
                    onClickPrimary={saveButtonClick}
                    disabled={errors || protocolConfigError || showFederationError || loaderBtnState}
                />
            )}
        </AppsProvider>
            {addingApp && (
                <Grid container justifyContent='flex-end' className='configpanel-row configpanel-butons'>
                    <Box pb={2} pt={2}>
                        <PasslyButton additionalColor='tertiary' className='modal__actions-tertiary' onClick={closeListModal} id='cancelAdd'>
                            {translation('CANCEL')}
                        </PasslyButton>
                    </Box>

                    <Box p={2}>
                        <PasslyButton
                            disabled={errors || protocolConfigError || showFederationError || loaderBtnState}
                            color='primary'
                            variant='contained'
                            onClick={saveButtonClick}
                            id='saveChanges'
                            isLoading={loaderBtnState}
                        >
                            {addingApp ? translation('ADD_APP') : translation('SAVE_CHANGES')}
                        </PasslyButton>
                    </Box>
                </Grid>
            )}
        </>
    );
};
